import dayjs from '../../../dayjs';

// Time periods classification for analytics
export const TIME_PERIODS = {
  MORNING: { start: '06:00', end: '10:00', label: 'Morning Peak' },
  MIDDAY: { start: '10:01', end: '15:00', label: 'Midday' },
  AFTERNOON: { start: '15:01', end: '19:00', label: 'Afternoon Peak' },
  EVENING: { start: '19:01', end: '23:59', label: 'Evening' },
  NIGHT: { start: '00:00', end: '05:59', label: 'Night' },
};

// Helper function to determine time period from a time string
export const getTimePeriod = (timeString) => {
  // Handle null, undefined or non-string values
  if (!timeString || typeof timeString !== 'string') {
    console.log('Invalid time string:', timeString, 'type:', typeof timeString);
    return 'unknown';
  }
  
  try {
    // Extract time part from datetime string
    let timePart = timeString;
    if (timeString.includes(' ')) {
      timePart = timeString.split(' ')[1];
    }
    
    // Default to empty string if splitting fails
    if (!timePart) return 'unknown';
    
    // Extract hours:minutes
    const timeForComparison = timePart.substring(0, 5);
    
    for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
      if (timeForComparison >= start && timeForComparison <= end) {
        return period;
      }
    }
    
    return 'unknown';
  } catch (error) {
    console.warn('Error determining time period:', error);
    return 'unknown';
  }
};

// Calculate on-time percentage for a given set of stops
export const calculateOnTimePercentage = (stops) => {
  if (!stops || stops.length === 0) return 0;
  
  const onTimeStops = stops.filter(stop => {
    // Check for departure variance if available
    if (stop.DEPARTURE_TIME_VARIANCE !== undefined && stop.DEPARTURE_TIME_VARIANCE !== null) {
      // Consider stops within 2 minutes of schedule as on-time
      return stop.DEPARTURE_TIME_VARIANCE >= -2 && stop.DEPARTURE_TIME_VARIANCE <= 2;
    }
    // Fall back to arrival variance if departure not available
    else if (stop.ARRIVAL_TIME_VARIANCE !== undefined && stop.ARRIVAL_TIME_VARIANCE !== null) {
      return stop.ARRIVAL_TIME_VARIANCE >= -2 && stop.ARRIVAL_TIME_VARIANCE <= 2;
    }
    return false;
  });
  
  return (onTimeStops.length / stops.length) * 100;
};

// Analyze trip performance and generate AI insights
export const analyzeTripPerformance = (trip) => {
  const insights = [];
  
  // Check first stop performance
  const firstStop = trip.stops.find(s => s.TSN_FIRST_LAST_IND === 'F');
  if (firstStop) {
    const variance = firstStop.DEPARTURE_TIME_VARIANCE || firstStop.ARRIVAL_TIME_VARIANCE;
    if (variance > 5) {
      insights.push(`Trip ${trip.TRIP_ID} consistently late at first stop — possible timetable issue`);
    }
  }
  
  // Check mid stops for patterns
  const midStops = trip.stops.filter(s => s.TSN_FIRST_LAST_IND === 'M');
  const lateMidStops = midStops.filter(s => {
    const variance = s.DEPARTURE_TIME_VARIANCE || s.ARRIVAL_TIME_VARIANCE;
    return variance > 5;
  });
  if (lateMidStops.length > midStops.length * 0.5) {
    insights.push(`Trip ${trip.TRIP_ID} shows consistent delays at mid stops — consider route timing adjustment`);
  }
  
  // Check last stop performance
  const lastStop = trip.stops.find(s => s.TSN_FIRST_LAST_IND === 'L');
  if (lastStop) {
    const variance = lastStop.DEPARTURE_TIME_VARIANCE || lastStop.ARRIVAL_TIME_VARIANCE;
    if (variance < -5) {
      insights.push(`Trip ${trip.TRIP_ID} often arrives early at last stop — possible schedule padding needed`);
    }
  }
  
  return insights;
};

// Analyze stop performance and generate AI insights
export const analyzeStopPerformance = (stop, historicalData) => {
  const insights = [];
  
  // Check for congestion patterns
  const timePeriod = getTimePeriod(stop.SCHEDULED_ARRIVAL_TIME_STRING);
  const periodData = historicalData.filter(d => 
    d.stopId === stop.stopId && 
    getTimePeriod(d.SCHEDULED_ARRIVAL_TIME_STRING) === timePeriod
  );
  
  const latePercentage = periodData.filter(d => d.ARRIVAL_TIME_VARIANCE > 5).length / periodData.length * 100;
  if (latePercentage > 70) {
    insights.push(`${timePeriod} congestion hotspot at Stop ${stop.stopId}`);
  }
  
  return insights;
};

// Analyze driver performance and generate AI insights
export const analyzeDriverPerformance = (driver, historicalData) => {
  const insights = [];
  
  // Check for early departure patterns
  const earlyDepartures = historicalData.filter(d => {
    const variance = d.DEPARTURE_TIME_VARIANCE || d.ARRIVAL_TIME_VARIANCE;
    return d.DRIVER_ID === driver.DRIVER_ID && variance < -5;
  });
  
  if (earlyDepartures.length > historicalData.length * 0.3) {
    insights.push(`Driver ${driver.DRIVER_ID} often departs early — consider OTR training`);
  }
  
  // Check for consistent lateness
  const lateArrivals = historicalData.filter(d => {
    const variance = d.DEPARTURE_TIME_VARIANCE || d.ARRIVAL_TIME_VARIANCE;
    return d.DRIVER_ID === driver.DRIVER_ID && variance > 5;
  });
  
  if (lateArrivals.length > historicalData.length * 0.3) {
    insights.push(`Driver ${driver.DRIVER_ID} shows consistent lateness — review route timing`);
  }
  
  return insights;
};

// Generate summary insights and recommendations
export const generateSummaryInsights = (tripAnalysis, stopData, driverAnalysis) => {
  console.log('Generating summary insights with:', {
    tripCount: tripAnalysis?.length || 0,
    stopCount: stopData?.length || 0,
    driverCount: driverAnalysis?.length || 0,
  });

  const insights = [];
  const recommendations = [];
  
  if (!tripAnalysis || !Array.isArray(tripAnalysis) || tripAnalysis.length === 0) {
    console.warn('No trip analysis data provided for insights');
    return { insights: [], recommendations: [] };
  }
  
  // Analyze route-level issues from trip data
  const routeIssues = analyzeRouteIssues(tripAnalysis);
  insights.push(...routeIssues.insights);
  recommendations.push(...routeIssues.recommendations);
  
  // Analyze driver-level issues
  if (driverAnalysis && Array.isArray(driverAnalysis) && driverAnalysis.length > 0) {
    const driverIssues = analyzeDriverIssues(driverAnalysis);
    insights.push(...driverIssues.insights);
    recommendations.push(...driverIssues.recommendations);
  }
  
  // Analyze stop-level issues
  if (stopData && Array.isArray(stopData) && stopData.length > 0) {
    const stopIssues = analyzeStopIssues(stopData);
    insights.push(...stopIssues.insights);
    recommendations.push(...stopIssues.recommendations);
  }
  
  const result = {
    insights: insights.slice(0, 5).map((insight, index) => ({
      title: `Issue #${index + 1}`,
      description: insight,
      severity: index < 2 ? 'high' : index < 4 ? 'medium' : 'low'
    })),
    recommendations: recommendations.slice(0, 5).map((rec, index) => ({
      title: `Recommendation #${index + 1}`,
      description: rec
    }))
  };

  console.log('Generated insights and recommendations:', {
    insightCount: result.insights.length,
    recommendationCount: result.recommendations.length
  });
  
  return result;
};

// Helper function to analyze route-level issues
const analyzeRouteIssues = (tripData) => {
  const insights = [];
  const recommendations = [];
  
  if (!tripData || !Array.isArray(tripData) || tripData.length === 0) {
    return { insights, recommendations };
  }
  
  // Group trips by route
  const routeGroups = {};
  tripData.forEach(trip => {
    const route = trip.route;
    if (!route) return;
    
    if (!routeGroups[route]) {
      routeGroups[route] = [];
    }
    routeGroups[route].push(trip);
  });
  
  // Analyze each route
  Object.entries(routeGroups).forEach(([route, trips]) => {
    // Route with low OTP
    const avgOnTimePercentage = trips.reduce((sum, trip) => {
      return sum + (trip.overallStats?.onTimePercentage || 0);
    }, 0) / trips.length;
    
    if (avgOnTimePercentage < 70) {
      insights.push(`Route ${route} has low on-time performance (${avgOnTimePercentage.toFixed(1)}%)`);
      recommendations.push(`Route ${route} needs schedule adjustment`);
    }
    
    // First stop issues
    const firstStopLatePercentage = trips.reduce((sum, trip) => {
      return sum + (trip.firstStopStats?.latePercentage || 0);
    }, 0) / trips.length;
    
    if (firstStopLatePercentage > 30) {
      insights.push(`Route ${route} has frequent late departures (${firstStopLatePercentage.toFixed(1)}%)`);
      recommendations.push(`Route ${route} needs increased dead-run time`);
    }
    
    // Last stop issues
    const lastStopEarlyPercentage = trips.reduce((sum, trip) => {
      return sum + (trip.lastStopStats?.earlyPercentage || 0);
    }, 0) / trips.length;
    
    if (lastStopEarlyPercentage > 30) {
      insights.push(`Route ${route} often arrives early at terminus (${lastStopEarlyPercentage.toFixed(1)}%)`);
      recommendations.push(`Adjust running time in Route ${route} timetable`);
    }
  });
  
  return { insights, recommendations };
};

// Helper function to analyze driver-level issues
const analyzeDriverIssues = (driverData) => {
  const insights = [];
  const recommendations = [];
  
  if (!driverData || !Array.isArray(driverData) || driverData.length === 0) {
    return { insights, recommendations };
  }
  
  // Analyze each driver performance
  driverData.forEach(driver => {
    if (!driver || !driver.driverId || !driver.overallStats) return;
    
    const driverId = driver.driverId;
    const stats = driver.overallStats;
    
    // Check on-time performance
    if (stats.onTimePercentage < 70) {
      insights.push(`Driver ${driverId} has low on-time performance (${stats.onTimePercentage.toFixed(1)}%)`);
      recommendations.push(`Driver ${driverId} requires OTR training`);
    }
    
    // Check for early running patterns
    if (stats.earlyPercentage > 30) {
      insights.push(`Driver ${driverId} frequently runs early (${stats.earlyPercentage.toFixed(1)}%)`);
      recommendations.push(`Driver ${driverId} needs training on maintaining schedule`);
    }
    
    // Check for late running patterns
    if (stats.latePercentage > 30) {
      insights.push(`Driver ${driverId} frequently runs late (${stats.latePercentage.toFixed(1)}%)`);
      recommendations.push(`Review routes operated by Driver ${driverId} for timing issues`);
    }
  });
  
  return { insights, recommendations };
};

// Helper function to analyze stop-level issues
const analyzeStopIssues = (stopData) => {
  const insights = [];
  const recommendations = [];
  
  if (!stopData || !Array.isArray(stopData) || stopData.length === 0) {
    return { insights, recommendations };
  }
  
  // Group by timing point stops
  const timingPoints = stopData.filter(stop => stop.IS_TIMING_POINT === true || stop.IS_TIMING_POINT === 1);
  
  // Calculate hotspots with late departures
  const stopGroups = {};
  timingPoints.forEach(stop => {
    // Skip if stop doesn't have a valid identifier
    if (!stop.TSN) return;
    
    const stopId = stop.TSN;
    if (!stopGroups[stopId]) {
      stopGroups[stopId] = [];
    }
    stopGroups[stopId].push(stop);
  });
  
  // Analyze each stop
  Object.entries(stopGroups).forEach(([stopId, stops]) => {
    if (stops.length < 5) return; // Need enough samples
    
    // Calculate percentage of late departures
    const lateDepartures = stops.filter(stop => {
      const variance = stop.DEPARTURE_TIME_VARIANCE || stop.ARRIVAL_TIME_VARIANCE;
      return variance > 5;
    }).length;
    const latePercentage = (lateDepartures / stops.length) * 100;
    
    if (latePercentage > 30) {
      // Get route and time period info for context
      const route = stops[0].ROUTE;
      const period = getTimePeriod(stops[0].SCHEDULED_ARRIVAL_TIME_STRING);
      
      insights.push(`Stop ${stopId} on Route ${route} has ${latePercentage.toFixed(1)}% late departures`);
      recommendations.push(`Review timing points near Stop ${stopId} during ${period}`);
    }
  });
  
  return { insights, recommendations };
}; 